<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '../../stores/authStore';
import { usePreferenceStore } from '../../stores/preferenceStore';

const { t } = useI18n();
const authStore = useAuthStore();
const preferenceStore = usePreferenceStore();

const onLocaleChange = (event: Event) => {
    preferenceStore.setLocale((event.target as HTMLSelectElement).value);
};

const triggerRef = ref<HTMLButtonElement | null>(null);
const dropdownRef = ref<HTMLDivElement | null>(null);
const open = ref(false);
const dropdownPosition = ref<{ top: number; right: number } | null>(null);

const user = computed(() => authStore.user);

const displayName = computed(() => {
    const u = user.value;
    if (!u) return null;
    const combined = `${u.first_name} ${u.last_name}`.trim();
    if (combined) return combined;
    return u.identifier || null;
});

const initials = computed(() => {
    const u = user.value;
    if (!u) return '?';
    const first = u.first_name.trim()[0];
    const last = u.last_name.trim()[0];
    if (first && last) return `${first}${last}`.toUpperCase();
    if (first) return first.toUpperCase();
    const id = u.identifier.trim();
    return id ? id[0]!.toUpperCase() : '?';
});

// If the display name was derived from first+last and the identifier is
// something different (typically an email), surface the identifier as a
// secondary line in the dropdown header.
const secondaryLine = computed(() => {
    const u = user.value;
    if (!u) return null;
    const id = u.identifier.trim();
    if (!id) return null;
    return id === displayName.value ? null : id;
});

const onTriggerClick = () => {
    open.value = !open.value;
};

const closeMenu = () => {
    open.value = false;
};

const onSignOut = () => {
    closeMenu();
    // Works under dev bypass too: clears the token and lands on the
    // /login splash (the backend skips the IdP), no re-auth loop.
    authStore.logout();
};

const onWindowClick = (event: MouseEvent) => {
    if (!open.value) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.user-menu')) return;
    closeMenu();
};

const onWindowKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && open.value) closeMenu();
};

// Anchor the dropdown to the trigger's right edge so it stays put if the
// header layout shifts. Recomputed on every open — cheap and avoids a
// resize listener.
watch(open, async (isOpen) => {
    if (!isOpen) {
        dropdownPosition.value = null;
        return;
    }
    await nextTick();
    const trigger = triggerRef.value;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    dropdownPosition.value = {
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
    };
});

onMounted(() => {
    window.addEventListener('click', onWindowClick);
    window.addEventListener('keydown', onWindowKeydown);
});

onBeforeUnmount(() => {
    window.removeEventListener('click', onWindowClick);
    window.removeEventListener('keydown', onWindowKeydown);
});
</script>

<template>
    <div v-if="user" class="user-menu">
        <button
            ref="triggerRef"
            type="button"
            class="user-menu-trigger"
            aria-haspopup="menu"
            :aria-expanded="open"
            :aria-label="
                displayName
                    ? t('userMenu.accountMenuFor', { name: displayName })
                    : t('userMenu.accountMenu')
            "
            @click="onTriggerClick"
        >
            <span class="user-menu-avatar">{{ initials }}</span>
        </button>

        <div
            v-if="open && dropdownPosition"
            ref="dropdownRef"
            class="user-menu-dropdown"
            :style="{
                top: dropdownPosition.top + 'px',
                right: dropdownPosition.right + 'px',
            }"
            role="menu"
        >
            <div class="user-menu-header">
                <div class="user-menu-header-name">
                    {{ displayName ?? t('userMenu.signedIn') }}
                </div>
                <div v-if="secondaryLine" class="user-menu-header-email">
                    {{ secondaryLine }}
                </div>
            </div>
            <div v-if="preferenceStore.languageSwitcherEnabled" class="user-menu-section">
                <label class="user-menu-section-label" for="user-menu-locale">
                    {{ t('userMenu.language') }}
                </label>
                <select
                    id="user-menu-locale"
                    class="user-menu-locale"
                    :value="preferenceStore.locale"
                    @change="onLocaleChange"
                >
                    <option
                        v-for="loc in preferenceStore.availableLocales"
                        :key="loc.code"
                        :value="loc.code"
                    >
                        {{ loc.label }}
                    </option>
                </select>
            </div>
            <button
                type="button"
                class="user-menu-item"
                role="menuitem"
                @click="onSignOut"
            >
                <span class="material-icons">logout</span>
                <span>{{ t('userMenu.signOut') }}</span>
            </button>
        </div>
    </div>
</template>

<style scoped>
.user-menu {
    margin-left: auto;
    position: relative;
}

.user-menu-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--radius-round);
    cursor: pointer;
    font-size: var(--text-base);
    color: var(--color-text-primary);
}

.user-menu-trigger:hover,
.user-menu-trigger:focus-visible {
    background: var(--color-secondary-hover);
    border-color: var(--color-border);
    outline: none;
}

.user-menu-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--radius-round);
    background: var(--color-secondary);
    color: var(--color-bg-white);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semibold);
    line-height: 1;
}

.user-menu-dropdown {
    position: fixed;
    z-index: var(--z-popover);
    background: var(--color-bg-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    min-width: 220px;
    padding: 4px 0;
    display: flex;
    flex-direction: column;
}

.user-menu-header {
    padding: 10px 16px;
    border-bottom: 1px solid var(--color-border);
}

.user-menu-header-name {
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    font-size: var(--text-base);
}

.user-menu-header-email {
    color: var(--color-text-secondary);
    font-size: var(--text-sm);
    margin-top: 2px;
    word-break: break-all;
}

.user-menu-section {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--color-border);
}

.user-menu-section-label {
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
}

.user-menu-locale {
    width: 100%;
    padding: 6px 8px;
    font-size: var(--text-base);
    color: var(--color-text-primary);
    background: var(--color-bg-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    cursor: pointer;
}

.user-menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: var(--text-base);
    color: var(--color-text-primary);
    text-align: left;
}

.user-menu-item:hover,
.user-menu-item:focus-visible {
    background: var(--color-row-hover);
    outline: none;
}

.user-menu-item .material-icons {
    font-size: 18px;
    color: var(--color-text-secondary);
}
</style>
